import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { Logo } from "@/components/Logo";

const sections = [
  {
    heading: "Our Service",
    body: [
      "Affra provides ticketing, event management, payment integration and related software services for event organisers, venues and promoters.",
      "We provide the technology infrastructure to facilitate ticket sales and event management. Unless expressly stated otherwise, Affra does not organise, promote or operate events.",
    ],
  },
  {
    heading: "Data Ownership",
    body: [
      "Your business data remains your property at all times.",
      "Affra does not claim ownership of your customer information, event data, sales data, reporting, analytics or business intelligence.",
      "You grant Affra only those rights necessary to provide the Services you have requested.",
    ],
  },
  {
    heading: "Our Commitment to Your Business",
    body: [
      "Trust is fundamental to our business.",
      "Accordingly, Affra commits that it will never knowingly:",
    ],
    list: [
      "Use your customer information to compete with your business;",
      "Use your sales information, reporting or business intelligence to create competing products, events or services;",
      "Sell or commercially exploit your customer data;",
      "License your customer information to third parties for commercial purposes;",
      "Monetise your customer information through advertising, profiling or data brokerage.",
    ],
    after: "Your information exists solely to enable the Services you have requested.",
  },
  {
    heading: "Business Sale or Change of Ownership",
    body: [
      "Customer information is not considered a commercial asset of Affra.",
      "Should Affra undergo a merger, acquisition, investment, restructuring or change of ownership, customer data will continue to be protected in accordance with our Privacy Policy, contractual obligations and applicable data protection legislation.",
      "In the event that any such change of ownership is to take place, Affra will notify affected customers no less than sixty (60) days before the acquisition, merger or takeover is due to complete, save where such advance notice is prohibited by law or regulatory requirement.",
      "Affra will never knowingly permit customer information to be transferred or used for the purpose of enabling a competing business to gain a commercial advantage over our clients.",
      "Where legally required, any successor organisation will be bound by the same confidentiality, privacy and security obligations that apply to Affra.",
    ],
  },
  {
    heading: "Acceptable Use",
    body: [
      "You agree not to use the Platform for any unlawful, fraudulent, deceptive or harmful purpose.",
      "All events listed on Affra must comply with applicable laws and regulations within the jurisdictions in which they are promoted and held.",
      "Affra reserves the right to suspend or remove any event or account that breaches these Terms.",
    ],
  },
  {
    heading: "Prohibited Events",
    body: ["The Platform must not be used to advertise, promote or facilitate events involving:"],
    list: [
      "Prostitution or the commercial exchange of sexual services;",
      "Sexual exploitation or trafficking;",
      "Non-consensual sexual activity;",
      "Escort services where payment is exchanged for sexual services;",
      "Illegal pornography or obscene material;",
      "Events involving the exploitation of minors;",
      "Illegal drug manufacture or distribution;",
      "Terrorism, organised crime or violent criminal activity;",
      "Fraudulent or deceptive schemes;",
      "Any activity prohibited by applicable law.",
    ],
    after: "Affra reserves the sole discretion to determine whether an event breaches these standards.",
  },
  {
    heading: "Adult Entertainment",
    body: [
      "The Platform must not be used to advertise, promote or facilitate events whose primary purpose is:",
    ],
    list: [
      "Sexual activity;",
      "Commercial sexual services;",
      "Prostitution;",
      "Solicitation for sexual services;",
      "Adult services involving physical sexual contact; or",
      "Any unlawful or exploitative adult activity.",
    ],
    after: "The organiser is solely responsible for ensuring that all applicable legal and licensing requirements are satisfied.",
  },
  {
    heading: "Social Events and Dating Activities",
    body: [
      "Affra recognises that many legitimate events are designed to help people meet, socialise and build meaningful relationships.",
      "Accordingly, we do allow individuals to meet potential mates, make friends, and engage in social activities that promote interaction and conversation. These may include singles mixers, speed dating events or other activities designed to bring people together for the purpose of dating, provided that the language used on the event website and promotional materials focuses on facilitating genuine, in-person social connections.",
      "Examples of permitted events include:",
    ],
    list: [
      "Mixer events;",
      "Social evenings;",
      "Community meetups;",
      "Friendship events;",
      "Hobby groups;",
      "Professional networking events.",
    ],
  },
  {
    heading: "Payments and Regulatory Compliance",
    body: [
      "Payments made through Affra are processed securely by approved third-party payment service providers that comply with recognised payment security standards, including PCI DSS where applicable.",
      "Affra does not store full payment card numbers, CVV/CVC security codes or PINs.",
      "Event organisers are solely responsible for ensuring that their business, payment activities and events remain fully compliant with all applicable laws, regulations and payment network requirements, including but not limited to:",
    ],
    list: [
      "Anti-Money Laundering (AML) legislation;",
      "Counter Terrorist Financing (CTF) requirements;",
      "Financial sanctions regulations;",
      "Consumer protection legislation;",
      "Tax legislation;",
      "Anti-bribery and anti-corruption laws;",
      "Payment processor and card scheme rules; and",
      "Any licensing or regulatory requirements applicable to their business.",
    ],
    after:
      "Organisers must maintain all licences, registrations, permits and approvals necessary to lawfully conduct their business and receive payments through the Platform. Affra reserves the right to request documentation or information necessary to satisfy legal, regulatory, Know Your Customer (KYC), Know Your Business (KYB) or payment partner compliance requirements at any time. Failure to maintain compliance, provide requested documentation, or comply with the requirements of our payment processing partners may result in suspension of payment processing; delayed settlement of funds where required by law or our payment partners; restrictions on Platform functionality; immediate offboarding from Affra; and/or termination of your account and this Agreement. Affra further reserves the right to decline, suspend or terminate payment processing where an organiser's activities present an unacceptable legal, regulatory, financial or reputational risk to the Platform, our payment partners or other users. Affra shall not be liable for any losses arising from an organiser's failure to comply with applicable laws, regulations or payment processing requirements.",
  },
  {
    heading: "How Our Fees Work (Plain English)",
    body: [
      "Affra does not charge Venues a subscription fee, a per-ticket fee to the venue, or a percentage of ticket revenue paid by the venue. We are only paid by the booking fee that ticket buyers pay at checkout.",
      "The booking fee is calculated as a percentage of the ticket face value with a minimum floor — 5% of face value with a £1.25 minimum in the United Kingdom, and 6% of face value with a $1.25 minimum in the United States. The exact percentage and minimum in effect at the time of sale is displayed on our pricing page.",
      "Out of that booking fee, we pay Stripe's card processing fees on your behalf on every ticket you sell. The full face value of every ticket lands in your Stripe account with no card processing fee deducted.",
      "In plain English: buyers pay the booking fee. We pay the card processing fee on every ticket. You keep 100% of the face value of every ticket you sell.",
    ],
    list: [
      "The booking fee applies on a per-ticket basis, calculated as the greater of (a) the applicable percentage of face value for the Venue's country, or (b) the applicable minimum booking fee for the Venue's country. It is displayed to the ticket buyer at checkout, either as a separate line item or, at the Venue's option, incorporated into the ticket's displayed price (a single all-in price).",
      "There is no maximum per-ticket price. Venues may set ticket face values freely, subject to compliance with applicable law and Stripe's acceptable use policies.",
      "Affra reserves the right to adjust the booking-fee percentage, the minimum booking fee, and any other commercial term on a prospective basis. Any such change will be published on our pricing page in advance and will not affect tickets that have already been sold.",
    ],
    after:
      "This arrangement operates within Stripe's standard Connect platform rules and requires no additional action from the Venue. The Venue's net financial exposure on card processing is zero.",
  },
  {
    heading: "Organiser Responsibilities",
    body: ["Event organisers are responsible for:"],
    list: [
      "The accuracy of all event information;",
      "Compliance with consumer protection legislation;",
      "Obtaining all necessary licences, permissions and insurance;",
      "Managing refunds and customer communications where applicable;",
      "Meeting all tax obligations;",
      "Complying with privacy and data protection legislation;",
      "Ensuring all events are conducted lawfully and safely.",
    ],
  },
  {
    heading: "Suspension and Termination",
    body: ["Affra may suspend, restrict or terminate any account where:"],
    list: [
      "These Terms have been breached;",
      "Fraud or unlawful activity is suspected;",
      "Regulatory or compliance concerns arise;",
      "Required documentation is not provided;",
      "Continued use presents a security, legal, financial or reputational risk.",
    ],
    after: "Termination under this section does not affect any legal rights or obligations that have accrued prior to termination.",
  },
  {
    heading: "Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, Affra shall not be liable for any indirect, incidental, consequential or special damages arising from the use of the Platform.",
      "Nothing within these Terms excludes liability where such exclusion is prohibited by applicable law.",
    ],
  },
  {
    heading: "Amendments",
    body: [
      "We may amend these Terms from time to time to reflect changes in our Services, legal obligations or operational requirements.",
      "The latest version will always be published on our website. Continued use of the Platform following publication of revised Terms constitutes acceptance of those amendments.",
    ],
  },
  {
    heading: "Governing Law",
    body: [
      "These Terms shall be governed by and interpreted in accordance with the laws of England and Wales.",
      "Any dispute arising under these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.",
    ],
  },
  {
    heading: "Contact",
    body: [
      "If you have any questions regarding these Terms of Service, please contact:",
      "Affra",
      "Email: legal@affra.io",
    ],
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-32 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid={NAV.logo}>
            <Logo size="h-28" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ legal</div>
        <h1 className="mt-3 font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none">
          Terms of Service
        </h1>
        <div className="mt-4 text-sm text-zinc-500">Effective Date: 10 July 2026</div>

        <p className="mt-8 text-[15px] leading-relaxed text-zinc-700">
          These Terms of Service (&quot;Terms&quot;) govern your access to and use of the Affra platform (&quot;Affra&quot;, &quot;Platform&quot;, &quot;Service&quot;, &quot;we&quot;, &quot;our&quot; or &quot;us&quot;). By creating an account or using our Services, you agree to be bound by these Terms.
        </p>

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <section key={s.heading} className="scroll-mt-24" id={`section-${i + 1}`}>
              <h2 className="font-display font-bold text-2xl tracking-tight flex gap-3 items-baseline">
                <span className="text-zinc-400 font-mono text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span>{s.heading}</span>
              </h2>
              <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-zinc-700 pl-9">
                {s.body.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
                {s.list && (
                  <ul className="list-disc pl-5 space-y-1.5 marker:text-zinc-400">
                    {s.list.map((li, idx) => (
                      <li key={idx}>{li}</li>
                    ))}
                  </ul>
                )}
                {s.after && <p>{s.after}</p>}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-200 text-sm text-zinc-500">
          Related: <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-sm text-zinc-500 flex-wrap gap-3">
          <div>© {new Date().getFullYear()} Affra — white-label ticketing.</div>
          <Link to="/" className="hover:text-zinc-900">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
